import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { createContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { default as courseRequests } from 'services/course';
import { v4 as uuid } from 'uuid';

const defaultForm = {
    courseName: '',
};

const PageContext = createContext();

const PageProvider = (props) => {
    const { updateCourse } = useApp();
    const { auth, changeAccount } = useAuth();
    const history = useHistory();
    const location = useLocation();
    const { t } = useTranslation('course');

    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({ ...defaultForm, course_id: uuid() });
    const [dialog, setDialog] = useState({
        open: false,
        message: '',
        description: '',
        variant: 'primary',
        actionButtonText: '',
        onAction: () => {},
    });
    const [errors, setErrors] = useState({
        courseName: '',
    });

    const handleChange = async (key, data) => {
        switch (key) {
            case 'courseName': {
                return setForm((prev) => ({ ...prev, courseName: data }));
            }
            default:
                return;
        }
    };

    const getErrorMessage = (key, data) => {
        switch (key) {
            case 'courseName': {
                const value = `${data || ''}`.trim();
                return value && value?.length > 0
                    ? ''
                    : t('course-setup.blank-error-message', 'Course name cannot be left blank');
            }
            default:
                return '';
        }
    };

    const validate = (key, value) => {
        const errorMessage = getErrorMessage(key, value, form);
        setErrors((errors) => {
            return {
                ...errors,
                [key]: errorMessage,
            };
        });
    };

    const validateAll = () => {
        return !Boolean(
            Object.keys(errors)
                .map((key) => {
                    validate(key, form[key]);
                    return getErrorMessage(key, form[key], form);
                })
                .reduce((cur, acc) => {
                    return cur + acc;
                }, '')
        );
    };

    const handleClose = async () => {
        setLoading(true);
        const data = {
            course_id: form.course_id,
            name: form.courseName,
            section: form.section,
            faculty: form.faculty,
            school:
                form.institutionName?.value === 'Other' && form.otherInstitutionName?.length > 0
                    ? form.otherInstitutionName
                    : form.institutionName?.value,
            semester: form.semester?.value,
            field_of_study: form.fieldOfStudy?.value,
            root_url: window.location.origin,
            finished_course_set_up: false,
        };
        // courseRequest.updateCourse(auth.course_id, data);
        updateCourse(data);
        return changeAccount();
    };

    const submitData = async () => {
        if (!validateAll()) return;
        setLoading(true);
        const data = {
            course_id: form.course_id,
            name: form.courseName,
            root_url: window.location.origin,
            finished_course_set_up: true,
        };
        await updateCourse(data);
        // await courseRequest.createCourse(auth.course_id, data);
        setLoading(false);
        return history.push('/');
    };

    useEffect(() => {
        if (location?.state?.duplicatingCourse) {
            setForm((current) => ({ ...current, courseName: location.state.duplicatingCourse.name }));
        }
    }, [location.state]);

    useEffect(() => {
        if (auth && auth.course_id) {
            (async () => {
                const existingCourseData = await courseRequests.findCourse(auth?.course_id);
                if (existingCourseData) {
                    setForm((current) => ({
                        ...current,
                        courseName: existingCourseData.name,
                        course_id: existingCourseData.id,
                    }));
                }
            })();
        }
    }, [auth, auth?.course_id]);

    return (
        <PageContext.Provider
            value={{
                loading,
                form,
                setForm,
                errors,
                setErrors,
                dialog,
                setDialog,
                handleChange,
                handleClose,
                submitData,
            }}
            {...props}
        />
    );
};

export { PageContext };
export default PageProvider;
